import { useEffect, useState } from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { ComboBox, ComboBoxFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { 
    Button, 
    DialogProps, 
    FieldInput, 
    FileUpload, 
    SubmitButton 
} from 'components';
import { AdjustmentType, DisputeType } from 'enums';
import { 
    useAdjustmentQuantity, 
    useAdjustmentTotal, 
    useDisputeRestrictions, 
    useFileUploadValidator, 
    useNavigation, 
    useRefreshOnDisputeCreated 
} from 'hooks';
import { useDisputeService } from 'services';
import { 
    useCurrentUser, 
    useDisputeState, 
    useSetDisputeState 
} from 'state';
import { AdjustmentDetail, InvoiceDetail, PaymentDetail } from 'types';
import styles from './NewDisputeForm.module.scss';

const reasonValidator = (value: string) => value ? '' : 'Required. Please select a dispute reason.';

const commentsValidator = (value: string) => value ? '' : 'Required. Please provide additional information about the dispute.';

const firstNameValidator = (value: string) => value ? '' : 'Required. Please enter the first name of the person to contact.';

const lastNameValidator = (value: string) => value ? '' : 'Required. Please enter the last name of the person to contact.';

const emailValidator = (value: string) => {
    if (!value) {
        return 'Required. Please enter the email address of the person to contact.';
    }
    if (!new RegExp(/\S+@\S+\.\S+/).test(value)) {
        return 'Please enter a valid email address.';
    }
    return '';
};

type NewDisputeProps = {
    className?: string,
    isPayment: boolean,
    adjustmentType: AdjustmentType,
};

export default function NewDisputeForm({ adjustmentType, className, isPayment }: NewDisputeProps) {
    //User information
    const currentUser = useCurrentUser();

    //Dispute information
    const disputeRows = useDisputeState();
    const disputeService = useDisputeService();
    const { availableDisputeReasons } = useDisputeRestrictions(disputeRows);
    const [disputeReasonOptions, setDisputeReasonOptions] = useState(availableDisputeReasons);
    const filesValidator = useFileUploadValidator();
    const refreshOnDisputeCreated = useRefreshOnDisputeCreated(disputeRows);

    const setDisputeState = useSetDisputeState();

    useEffect(() => {
        setDisputeReasonOptions(availableDisputeReasons);
    }, [availableDisputeReasons]);

    const onDisputeReasonFilterChange = ({ filter: { value } }: ComboBoxFilterChangeEvent) => {
        const search = value?.trim().toLowerCase() ?? '';
        setDisputeReasonOptions(
            search
                ? availableDisputeReasons.filter(reason => reason.description.toLowerCase().includes(search))
                : availableDisputeReasons
        );
    };

    const handleQuantityChange = (event: any) => {
        const { value } = event.target;
        setDisputeState((prevRows: AdjustmentDetail[]) => {
            return prevRows.map((row, idx) =>
                idx === 0
                    ? { ...row, disputedQuantity: Number(value) }
                    : row
            );
        });
    }

    // Constant needed to limit the amount users can dispute 
    // to be equal or less than the total of the adjustments.
    const getTotal = useAdjustmentTotal(disputeRows);
    const maxAmount = Math.abs(getTotal(adjustmentType));
    const disputeAmountValidator = (value: string) => value ? '' : `Required. Please enter a dollar amount between $0.01 and ${maxAmount}`;

    // If the adjustment type is Quantity, we need to calculate the unit price based on the first row.
    const unitPrice = adjustmentType === AdjustmentType.Quantity ? (disputeRows[0] as InvoiceDetail).adjustmentUnitPrice : 0;

    const getQuantity = useAdjustmentQuantity(disputeRows as InvoiceDetail[]);
    const quantity = Math.abs(getQuantity(adjustmentType));
    const maxQuantity = quantity * unitPrice <= maxAmount ? quantity : (maxAmount / unitPrice).toFixed(2);
    const disputeQuantityValidator = (value: string) => value && value <= maxQuantity ? ''
        : `Required. Please enter a quantity between 1 and ${maxQuantity}`;

    // Constants needed to show the submit confirmation dialog
    // window under success and error conditions. 
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    // PRM duplicate claim fix (2026-08-24): The API can return an actionable
    // duplicate message; retain it for the error dialog instead of hiding it.
    const [errorMessage, setErrorMessage] = useState<string>();

    // Constant to show the spinner while waiting for the
    // CREATE DISPUTE API call to complete.
    const [submitting, setSubmitting] = useState<boolean>(false);

    const [disputeNumberCreated, setDisputeNumberCreated] = useState<string>('');

    // Function that handles the form submit button.
    const handleSubmit = async (dataItem: { [name: string]: any }) => {
        if (submitting) return;
        setSubmitting(true);
                
        try {
            // Get the form data from dispute context and form fields. 
            const firstRow = disputeRows[0];
            const paymentRow = firstRow as PaymentDetail;
            const invoiceRow = firstRow as InvoiceDetail;
            const voucherPoId = paymentRow.voucherPoId?.toString().trim();
            const marPoReference = paymentRow.marPoReference?.trim();
            const poNumber = isPayment
                ? (voucherPoId && voucherPoId !== '0'
                    ? voucherPoId
                    : marPoReference)
                : invoiceRow.poId;

            const newDisputeData = {
                disputeType: isPayment ? DisputeType.Payment : DisputeType.Invoice,
                accountId: firstRow.accountId,
                vendorId: firstRow.vendorId,
                disputeAmount: firstRow.adjustmentTypeId === AdjustmentType.Quantity ? Number(dataItem.disputeQuantity) * unitPrice : dataItem.disputeAmount,
                contactFirstName: dataItem.contactFirstName,
                contactLastName: dataItem.contactLastName,
                contactEmail: dataItem.contactEmail,
                disputeReasonId: dataItem.disputeReason?.disputeReasonId,
                comments: dataItem.moreDisputeInfo,
                adjustmentTypeId: firstRow.adjustmentTypeId,
                paymentIdRef: isPayment ? firstRow.paymentIdRef : null,
                invoiceId: !isPayment ? firstRow.invoiceId : null,
                poNumber,
                paymentDetailLines: isPayment ? disputeRows : [],
                invoiceDetailLines: !isPayment ? disputeRows : []
            };

            // Get the supporting docs files uploaded by the user.
            const fileData = dataItem.supportingDocumentation?.map((file: any) => file.getRawFile()) ?? [];

            // Make the API POST request to create a new dispute.
            const response = await disputeService?.submitDispute(newDisputeData, fileData);

            if (response.status) {
                // Show the confirmation dialog window for success.
                setShowConfirmation(true);
                setDisputeNumberCreated(response.disputeNumber)
                refreshOnDisputeCreated();
            } else {
                setErrorMessage(response.errorMessage ?? 'Something went wrong while creating the dispute.');
                setShowError(true);
            }
        } catch (error) {
            setErrorMessage('Something went wrong while creating the dispute.');
            setShowError(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={className}>
            <Form
                onSubmit={handleSubmit}
                initialValues={{
                    contactFirstName: currentUser?.firstName,
                    contactLastName: currentUser?.lastName,
                    contactEmail: currentUser?.emailAddress
                }}
                render={({ valid, valueGetter }) => (
                    <FormElement className={styles.form}>
                        {adjustmentType !== AdjustmentType.Discount && (
                            <div className={styles.item}>
                                <label
                                    htmlFor="disputeReason"
                                    id="disputeReasonLabel"
                                    className={styles.hidden} >
                                    Select dispute reason.
                                </label>
                                <Field
                                    className={styles.field}
                                    component={FieldInput}
                                    child={ComboBox}
                                    label="Why are you disputing the adjustment?"
                                    name="disputeReason"
                                    data={disputeReasonOptions}
                                    textField="description"
                                    dataItemKey="disputeReasonId"
                                    suggest
                                    allowCustom={false}
                                    filterable
                                    onFilterChange={onDisputeReasonFilterChange}
                                    onOpen={() => setDisputeReasonOptions(availableDisputeReasons)}
                                    popupSettings={{
                                        popupClass: styles.reasonPopup,
                                        width: 'min(32rem, calc(100vw - 2rem))',
                                        height: '20rem'
                                    }}
                                    placeholder="Use drop-down arrow or start typing..."
                                    ariaLabelledBy="disputeReasonLabel"
                                    ariaDescribedBy="disputeReason_label"
                                    required={true}
                                    validator={reasonValidator}
                                />
                            </div>
                        )}

                        <div className={styles.item}>
                            <label
                                htmlFor="disputeAmount"
                                id="disputeAmountLabel"
                                className={styles.hidden} >
                                Enter dispute amount
                            </label>
                            {disputeRows[0].adjustmentTypeId !== AdjustmentType.Quantity ?
                                <Field
                                    component={FieldInput}
                                    child={NumericTextBox}
                                    className={styles.field}
                                    label="Enter the amount you would like to dispute (enter digits only)"
                                    name="disputeAmount"
                                    format="c2"
                                    max={maxAmount}
                                    min={0.01}
                                    spinners={false}
                                    type="number"
                                    placeholder="e.g., 620.75"
                                    ariaLabelledBy="disputeAmountLabel"
                                    ariaDescribedBy="disputeAmount_label"
                                    required={true}
                                    validator={disputeAmountValidator}
                                /> :
                                <>
                                    <Field
                                        component={FieldInput}
                                        child={NumericTextBox}
                                        className={styles.field}
                                        label="Enter the quantity you would like to dispute (enter digits only)"
                                        name="disputeQuantity"
                                        format="n0"
                                        max={maxQuantity}
                                        min={1}
                                        spinners={false}
                                        type="number"
                                        placeholder="e.g., 5"
                                        summary="This is the quantity of the adjustment you are disputing."
                                        ariaLabelledBy="disputeQuantityLabel"
                                        ariaDescribedBy="disputeQuantity_label"
                                        required={true}
                                        validator={disputeQuantityValidator}
                                        onChange={handleQuantityChange}
                                    />
                                    {valueGetter('disputeQuantity') && 
                                        <span>Total: ${valueGetter('disputeQuantity') * unitPrice}</span>}
                                </>
                            }
                        </div>

                        <div className={styles.item}>
                            <label
                                htmlFor="moreDisputeInfo"
                                id="moreDisputeInfoLabel"
                                className={styles.hidden} >
                                Enter additional information
                            </label>
                            <Field
                                component={FieldInput}
                                child={TextArea}
                                className={styles.field}
                                label="Please provide additional information about the dispute"
                                name="moreDisputeInfo"
                                required={adjustmentType === AdjustmentType.DotBillback}
                                rows={4}
                                autoSize={true}
                                validityStyles={false}
                                ariaLabelledBy="moreDisputeInfoLabel"
                                ariaDescribedBy="moreDisputeInfo_label"
                                validator={commentsValidator}
                            />
                        </div>

                        <div className={styles.item}>
                            <label
                                htmlFor="supportingDocumentation"
                                id="supportingDocumentationLabel"
                                className={styles.hidden} >
                                Upload documents
                            </label>
                            <Field
                                component={FieldInput}
                                child={FileUpload}
                                className={styles.field}
                                label="Please attach any supporting documentation (20 files max)"
                                name="supportingDocumentation"
                                ariaLabelledBy="supportingDocumentationLabel"
                                ariaDescribedBy="supportingDocumentation_label"
                                validator={filesValidator}
                            />
                        </div>

                        <div className={styles.item}>
                            <div className={styles.fontWeight600}
                                id="contactDescription" role="group">
                                Who should we contact about this dispute?
                            </div>
                            <Field
                                component={FieldInput}
                                child={Input}
                                className={styles.field}
                                label="First Name"
                                labelClassName={styles.contactLabel}
                                name="contactFirstName"
                                placeholder="e.g., Pat"
                                autoComplete="off"
                                ariaDescribedBy="contactDescription"
                                required={true}
                                validator={firstNameValidator}
                            />
                            <Field
                                component={FieldInput}
                                child={Input}
                                className={styles.field}
                                label="Last Name"
                                labelClassName={styles.contactLabel}
                                name="contactLastName"
                                placeholder="e.g., Smith"
                                autoComplete="off"
                                ariaDescribedBy="contactDescription"
                                required={true}
                                validator={lastNameValidator}
                            />
                            <Field
                                component={FieldInput}
                                child={Input}
                                className={styles.field}
                                label="Email"
                                labelClassName={styles.contactLabel}
                                name="contactEmail"
                                placeholder="e.g., pat.smith@greatness.com"
                                type="email"
                                ariaDescribedBy="contactDescription"
                                autoComplete="off"
                                required={true}
                                validator={emailValidator}
                            />
                        </div>

                        <div className={styles.buttonContainer}>
                            <SubmitButton
                                disabled={!valid || submitting}
                                label="SUBMIT DISPUTE"
                                submittingLabel="Submitting"
                                isSubmitting={submitting}
                                themeColor="primary"
                            />
                        </div>

                        {showConfirmation && (
                            <ConfirmationDialog
                                disputeNumber={disputeNumberCreated}
                                isPayment={isPayment}
                                onClose={() => setShowConfirmation(false)}
                            />
                        )}
                        {showError && <ErrorDialog message={errorMessage} onClose={() => setShowError(false)} />}

                    </FormElement>
                )}
            />
        </div>
    );
}

type ConfirmationProps = {
    disputeNumber: string,
    isPayment: boolean,
} & DialogProps;

const ConfirmationDialog = ({ disputeNumber, isPayment, onClose }: ConfirmationProps) => {
    const { navigateToDispute, navigateToInvoices, navigateToPayments } = useNavigation({ replace: true});
    const navigateToNextPage = () => {
        isPayment ? navigateToPayments() : navigateToInvoices();
    };

    return (
        <Dialog
            className={styles.dialog}
            autoFocus={true}
            onClose={onClose}
        >
            <div className={styles.content}>
                <div className={styles.title}>
                    Thank You
                </div>
                <div className={styles.message}>
                    The dispute number {disputeNumber} has been successfully created! Click the View Dispute button below to view it or OK to return to the {isPayment ? 'payment' : 'invoice'} page.
                </div>
            </div>
            <DialogActionsBar>
                <div className={styles.confirmationContainer}>
                    <Button
                        fillMode="solid"
                        themeColor="primary"
                        onClick={() => navigateToDispute(disputeNumber)}
                    >
                        VIEW DISPUTE
                    </Button>
                    <Button
                        fillMode="outline"
                        themeColor="secondary"
                        className={styles.okButton}
                        onClick={navigateToNextPage}
                    >
                        OK
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

type ErrorDialogProps = DialogProps & {
    message?: string;
};

// Keep the existing error-dialog layout while allowing server-side validation
// messages, such as duplicate-dispute conflicts, to be displayed to the user.
const ErrorDialog = ({ message, onClose }: ErrorDialogProps) => (
    <Dialog
        className={styles.dialog}
        autoFocus={true}
        onClose={onClose}
    >
        <div className={styles.content}>
            <div className={styles.title}>
                Oops!
            </div>
            <div className={styles.message}>
                {message ?? 'Something went wrong. Please click the "Try Again" button below, review the form, and submit it again. If you continue to experience problems, please contact your Dot Foods representative.'}
            </div>
        </div>
        <DialogActionsBar>
            <div className={styles.button}>
                <Button
                    fillMode="solid"
                    themeColor="primary"
                    onClick={onClose}
                >
                    Try Again
                </Button>
            </div>
        </DialogActionsBar>
    </Dialog>
);
